import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClinicRankRow, ZoneSummary } from '@/dashboard/types';
import { fetchAgmCallActionSummary, fetchAgmDashboard } from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { CallActionWorkbenchPanel } from '@/dashboard/CallActionWorkbenchPanel';
import { FIXTURE_BACKED_PANELS_ENABLED } from '@/dashboard/fixture-panels';
import ManagerTrackingPanel from '@/dashboard/manager-tracking/ManagerTrackingSection';
import ManagerOpsCockpit, {
  SLA_SECTION_ANCHOR_ID,
} from '@/dashboard/manager-tracking/ManagerOpsCockpit';
import SlaLeakagePanel from '@/dashboard/sla-leakage/SlaLeakageSection';
import StationManagerPanel from '@/dashboard/coc/StationManagerPanel';

/** Below this measured percent the zone billing-compliance tile reads danger
 *  (the pre-existing AGM threshold; billingCompliance is a 0-100 percentage). */
export const AGM_BILLING_COMPLIANCE_WARN_THRESHOLD = 90;

export interface AgmBillingComplianceView {
  /** The tile value: a real percent, or the honest "Not available". */
  readonly display: string;
  /** The honest sub-line for an unavailable state, or null when measured. */
  readonly sub: string | null;
  /** Danger tone -- reserved for a TRUE breach among reported days. */
  readonly warn: boolean;
}

/**
 * MANAGEMENT-READ-TRUTH-V1: the AGM zone billing-compliance tile follows the
 * OWNER tile's THREE-STATE LAW (OwnerDashboardPage.tsx + money-completion.test
 * are the ready pattern; owner-tile vocabulary reproduced verbatim):
 *   1. MIS rollup NEVER received (misRollup present, receivedEver !== true)
 *        -> "Not available" (neutral) -- compliance measures submitted rollups;
 *   2. no rollup for THIS date (also the honest default when misRollup is
 *        absent) -> "Not available" (neutral) -- an empty clinic day starts
 *        neutral, never a warn-toned 0%;
 *   3. rollup present + measured -> the real percent; danger (warn) ONLY when a
 *        true breach reads below the threshold, ok otherwise.
 * Pure; no fetch. Mirrors the doctor-home-view.ts view-helper idiom.
 */
export function agmBillingComplianceView(
  zone: Pick<ZoneSummary, 'billingCompliance' | 'reportDate' | 'misRollup'>,
): AgmBillingComplianceView {
  const misRollup = zone.misRollup ?? null;
  if (misRollup && misRollup.receivedEver !== true) {
    return {
      display: 'Not available',
      sub: 'MIS rollup not received yet - compliance measures submitted rollups.',
      warn: false,
    };
  }
  if (!misRollup || misRollup.receivedForDate !== true) {
    return {
      display: 'Not available',
      sub: `No billing rollup submitted for ${zone.reportDate} yet - an empty clinic day starts neutral, never in breach.`,
      warn: false,
    };
  }
  return {
    display: `${zone.billingCompliance.toFixed(0)}%`,
    sub: null,
    warn: zone.billingCompliance < AGM_BILLING_COMPLIANCE_WARN_THRESHOLD,
  };
}

export default function AgmDashboardPage() {
  const [zone, setZone] = useState<ZoneSummary | null>(null);
  const [rankings, setRankings] = useState<ClinicRankRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAgmDashboard()
      .then(({ zone: z, rankings: r }) => {
        if (!cancelled) {
          setZone(z);
          setRankings(r);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setZone(null);
          setRankings([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const z = zone;
  const billingView = z ? agmBillingComplianceView(z) : null;

  return (
    <div className="agm-dashboard">
      <div className="page-header">
        <h1>{z?.zoneName ?? 'My Zone'}</h1>
        <p className="page-header__sub">{z?.reportDate ?? '—'}</p>
        {/* TRAIN-B (SCOPE 6): the read-down drill into ONE clinic's day --
            the same view-only day board the front desk runs (the header
            clinic picker chooses which in-scope clinic). A drill link, not
            a new nav item (doctrine rule 3). */}
        <p className="page-header__sub">
          <Link to="/walkin" data-testid="agm-clinic-day-drill">
            Open the clinic day board
          </Link>{' '}
          - today&apos;s appointments end to end at one clinic (view-only).
        </p>
      </div>

      {/*
        MANAGER-OPS-COCKPIT-V2 (V1-P6): the TODAY COMMAND VIEW -- the V1-P4
        tracking surface re-scoped in place; the 30-second manager scan lands
        FIRST (money-first hierarchy). Self-fetching + fault-isolated over the
        EXISTING day-board / manager-tracking / sla-leakage reads; a router,
        never an editor (zero write actions).
      */}
      <ManagerOpsCockpit />

      {/*
        MANAGER-TRACKING-VIEW-V1 (V1-P4): the J2/J3 ops.queue.matrix landing
        section (surface fate 04-SURFACE-FATES row 37). Self-fetching +
        fault-isolated over GET /api/manager/tracking; read-only leadership
        aggregate from the ONE lib/queue-health computation. The cockpit's
        supporting depth (clinic x metric matrix + per-staff load).
      */}
      <ManagerTrackingPanel />

      {/*
        TRAIN-D-V4-P0-SLA-LEAKAGE-ENGINE-V1: the read-only work-leakage queue
        section (Train D). Self-fetching + fault-isolated + AUDIENCE-GATED
        (me.slaLeakageEnabled, a flag-free server-computed audience signal; the
        internal-surface env flag was retired) -- renders NOTHING for a role
        outside the leakage audience. A SECTION on this existing surface, not a
        new nav destination (J1 five-link law). SEE-NOT-DO: read-only; V4-P1
        adds the assign/resolve actions. The anchor id is the cockpit SLA
        tiles' in-page drill target.
      */}
      <div id={SLA_SECTION_ANCHOR_ID}>
        <SlaLeakagePanel />
      </div>

      {/*
        COC-STATION-V1 (V4-P1, Scope 2): per-operator workload +
        accountability over the caller's own scope -- assigned/overdue/
        callbacks/outcomes + conversion-by-operator (THE owner-funnel
        converted-set derivation, consumed verbatim server-side) +
        unclaimed-past-escalation + needs-clinic-assignment, each count a
        drill to a role-held surface (LES-016). A SECTION on this existing
        manager surface, not a new nav destination (J1 five-link law).
        Audience = MANAGER_TRACKING_AUDIENCE_ROLES exactly (server-gated);
        COC admission stays a named seam pending tenant question 7.
      */}
      <StationManagerPanel />

      {error ? (
        <div className="mb-4">
          <DashboardLoadError message={error} />
        </div>
      ) : null}

      {z ? (
        <>
          <div className="zone-kpis">
            <div className="zone-kpi">
              <span className="zone-kpi__value">₹{z.totalRevenue.toLocaleString('en-IN')}</span>
              <span className="zone-kpi__label">Zone revenue</span>
              <span
                className={`zone-kpi__trend ${z.weeklyTrend > 0 ? 'positive' : z.weeklyTrend < 0 ? 'negative' : ''}`}
              >
                {z.weeklyTrend > 0 ? '↑' : z.weeklyTrend < 0 ? '↓' : '—'}{' '}
                {Math.abs(z.weeklyTrend).toFixed(1)}% vs last week
              </span>
            </div>
            <div className="zone-kpi">
              <span className="zone-kpi__value">{z.totalPatients}</span>
              <span className="zone-kpi__label">Patients today</span>
            </div>
            <div className={`zone-kpi ${z.avgTicketSize < z.targetTicketSize ? 'zone-kpi--warn' : ''}`}>
              <span className="zone-kpi__value">₹{z.avgTicketSize.toLocaleString('en-IN')}</span>
              {/* SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 3, founder ruling
                  2026-09-04): "Ticket size" is ONE Owner meaning -- revenue /
                  walk-ins (OWNER-INTERVIEW-2026-08-02 lines 43-44), rendered on
                  the money console. This zone figure is a DIFFERENT
                  denominator -- MIS revenue over billed patients (agm.ts: sum
                  total_revenue / sum billed_patient_count) -- so it carries a
                  name that states that denominator instead of a second
                  "ticket" reading. The MATHS IS UNCHANGED; only the words
                  moved. */}
              <span className="zone-kpi__label">Avg per billed patient</span>
              <span className="text-xs text-muted-foreground">
                Target ₹{z.targetTicketSize.toLocaleString('en-IN')}
              </span>
            </div>
            {/* MANAGEMENT-READ-TRUTH-V1: the three-state billing-compliance
                tile (owner-tile ready pattern) -- an empty clinic day reads a
                neutral "Not available", never a warn-toned 0%; danger is
                reserved for a true breach among reported days. */}
            <div className={`zone-kpi ${billingView?.warn ? 'zone-kpi--warn' : ''}`}>
              <span className="zone-kpi__value">{billingView?.display}</span>
              <span className="zone-kpi__label">Billing compliance</span>
              {billingView?.sub != null ? (
                <span className="text-xs text-muted-foreground">{billingView.sub}</span>
              ) : null}
            </div>
          </div>

          <div className="ranking-section mb-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-base font-semibold">Clinic rankings</h2>
              <Link to="/dashboard/agm/compare" className="text-sm text-primary underline-offset-4 hover:underline">
                Compare clinics →
              </Link>
            </div>
            {rankings.length > 0 ? (
              <table className="ranking-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Clinic</th>
                    <th>Revenue</th>
                    <th>Patients</th>
                    {/* FIX 3 wording parity: the same MIS-denominator
                        figure as the zone tile above, so the same name. */}
                    <th>Avg per billed patient</th>
                    <th>No-show</th>
                    <th>Alerts</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((r) => (
                    <tr key={r.clinicId}>
                      <td>{r.rank}</td>
                      <td>{r.clinicName}</td>
                      <td>{r.revenue.toLocaleString('en-IN')}</td>
                      <td>{r.patients}</td>
                      <td>{r.avgTicket.toLocaleString('en-IN')}</td>
                      <td>{(r.noShowRate * 100).toFixed(1)}%</td>
                      <td>{r.hasAlerts ? '⚠' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground">No clinics in zone for today.</p>
            )}
          </div>
        </>
      ) : null}

      {/*
        CRM-ACTION-WORKBENCH-PANEL-01: GM/AGM zone-scoped Call Action
        Workbench. Self-fetching + fault-isolated; consumes the existing
        /api/agm/call-action-summary endpoint (no backend change). Its only
        data path today is sample/fixture-backed, so it is kept out of the
        normal journey by the shared FIXTURE_BACKED_PANELS_ENABLED guardrail
        (default off) and a product-safe placeholder renders in its place.
      */}
      {!error && FIXTURE_BACKED_PANELS_ENABLED ? (
        <CallActionWorkbenchPanel
          fetcher={fetchAgmCallActionSummary}
          title="Zone Call Action Workbench"
        />
      ) : !error ? (
        <section
          className="clinic-table"
          aria-label="Zone call activity"
          data-testid="agm-call-activity-placeholder"
        >
          <h2 className="clinic-table__title">Call activity</h2>
          <p className="clinic-table__subtitle">
            Call activity will appear here when available.
          </p>
        </section>
      ) : null}

      {loading ? <div className="loading">Loading zone…</div> : null}
    </div>
  );
}
